import type {
  ResolvedUiConfig,
  ResolvedUiConfigSurfaceItem,
  UiConfigBase,
  UiConfigOverlay,
  UiConfigOverlaySurface,
} from "./document.js";

/**
 * Base ⊕ overlay merge engine.
 *
 * Guarantees (property-tested in merge.test.ts):
 *
 * 1. Total: never throws for any schema-valid base + overlay.
 * 2. Conservation: each resolved surface contains exactly the base's item
 *    ids — stale overlay ids are ignored, missing ones are re-inserted.
 *    Hiding is a flag, never removal.
 * 3. Order: ids present in both overlay.order and the base appear in
 *    overlay.order's relative order. Base ids absent from overlay.order
 *    stay anchored to their nearest preceding ordered neighbor (in base
 *    order), so items added by a base upgrade appear where the base put
 *    them relative to the user's arrangement.
 * 4. Read-only: the stored overlay is never mutated or rewritten by a
 *    merge; migration/cleanup of stale ids is a separate, explicit write.
 */

function mergeSurfaceOrder(
  baseItems: readonly string[],
  overlayOrder: readonly string[] | undefined,
): string[] {
  if (overlayOrder === undefined || overlayOrder.length === 0) {
    return [...baseItems];
  }

  const baseSet = new Set(baseItems);
  const ordered: string[] = [];
  const orderedSet = new Set<string>();
  for (const id of overlayOrder) {
    if (baseSet.has(id) && !orderedSet.has(id)) {
      ordered.push(id);
      orderedSet.add(id);
    }
  }
  if (ordered.length === 0) {
    return [...baseItems];
  }

  // Anchor each unordered base id to its nearest preceding ordered id in
  // base order; unordered ids that precede every ordered id go up front.
  const trailing = new Map<string, string[]>();
  const leading: string[] = [];
  let lastOrdered: string | null = null;
  for (const id of baseItems) {
    if (orderedSet.has(id)) {
      lastOrdered = id;
      continue;
    }
    if (lastOrdered === null) {
      leading.push(id);
    } else {
      const bucket = trailing.get(lastOrdered);
      if (bucket === undefined) {
        trailing.set(lastOrdered, [id]);
      } else {
        bucket.push(id);
      }
    }
  }

  const result: string[] = [...leading];
  for (const id of ordered) {
    result.push(id, ...(trailing.get(id) ?? []));
  }
  return result;
}

function resolveSurface(
  baseItems: readonly string[],
  overlaySurface: UiConfigOverlaySurface | undefined,
): ResolvedUiConfigSurfaceItem[] {
  const order = mergeSurfaceOrder(baseItems, overlaySurface?.order);
  const hidden = new Set(overlaySurface?.hidden ?? []);
  return order.map((id) => ({ id, hidden: hidden.has(id) }));
}

export function resolveUiConfig(
  base: UiConfigBase,
  overlay: UiConfigOverlay,
): ResolvedUiConfig {
  const surfaces: ResolvedUiConfig["surfaces"] = {};
  for (const [surfaceId, baseSurface] of Object.entries(base.surfaces)) {
    surfaces[surfaceId] = {
      items: resolveSurface(baseSurface.items, overlay.surfaces?.[surfaceId]),
    };
  }

  return {
    lenses: {
      privacy: {
        enabled: overlay.lenses?.privacy?.enabled ?? base.lenses.privacy.enabled,
      },
      denomination: {
        currency:
          overlay.lenses?.denomination?.currency ??
          base.lenses.denomination.currency,
      },
      density: {
        mode: overlay.lenses?.density?.mode ?? base.lenses.density.mode,
      },
    },
    surfaces,
  };
}
